import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import {
  CdwDropdownMenuModule,
  CdwDropdownMenuOption,
} from '@cdw-ae/common-ui-components-dropdown-menu';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { selectcurrentUrl } from '../../../../+state/app-settings.selectors';
import { RootState } from '../../../../common/interfaces/rootState.interface';
import type { NavbarActionButton } from './interfaces/navbar-action-button.interface';
import { NavbarActionButtonService } from './services/navbar-action-button.service';

@Component({
  selector: 'navbar-action-buttons',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialLibraryModule,
    MatIconModule,
    MatTooltipModule,
    CdwDropdownMenuModule,
    CdwButtonModule,
  ], // ExportWizardModule removed temporarily
  providers: [], // GridExportsService removed temporarily
  templateUrl: './navbar-action-buttons.component.html',
  styleUrls: ['./navbar-action-buttons.component.scss'],
})
export class NavbarActionButtonsComponent implements OnInit, OnDestroy {
  private store: Store<RootState> = inject(Store);
  private navbarButtonService = inject(NavbarActionButtonService);
  private destroy$ = new Subject<void>();

  currentUrl$: Observable<string | null> = this.store.select(selectcurrentUrl);

  // Observable of dynamic buttons for current route
  public dynamicButtons$: Observable<NavbarActionButton[]> = this.currentUrl$.pipe(
    switchMap((route) => this.navbarButtonService.getButtonsForRoute(route)),
    takeUntil(this.destroy$)
  );

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    // Component initialization - buttons are registered by individual pages
  }

  /**
   * Check if a button should be disabled
   * Uses the button's own isDisabled function if provided
   */
  public isButtonDisabled(button: NavbarActionButton): boolean {
    if (button.isDisabled) {
      return button.isDisabled();
    }
    return false;
  }

  /**
   * Handle dropdown option selection for dynamic buttons
   */
  public handleOptionSelected(button: NavbarActionButton, option: CdwDropdownMenuOption): void {
    if (button.onOptionSelected && !this.isButtonDisabled(button)) {
      button.onOptionSelected(option);
    }
  }

  /**
   * Handle simple button click for dynamic buttons
   */
  public handleButtonClick(button: NavbarActionButton): void {
    if (button.onClick && !this.isButtonDisabled(button)) {
      button.onClick();
    }
  }
}
